import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Wallet, Search, Plus, Minus, History } from 'lucide-react';
import { AdminPageHeader, AdminPageShell, AdminPageBody } from '../../components/AdminPageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const formatCpf = (c) => {
  const d = (c || '').replace(/\D/g, '').padStart(11, '0').slice(-11);
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`;
};

const AdminCreditBags = () => {
  const navigate = useNavigate();
  const [allBags, setAllBags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCpf, setSearchCpf] = useState('');
  const [selectedData, setSelectedData] = useState(null);
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ cpf: '', kind: 'normal', amount: '', type: 'credit', note: '' });
  const [msg, setMsg] = useState(null);
  // Novos: totais consolidados + colapsar listagem + filtros
  const [totals, setTotals] = useState({ normal_total: 0, limited_total: 0, normal_count: 0, limited_count: 0 });
  const [showList, setShowList] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [listKindFilter, setListKindFilter] = useState('all'); // 'all'|'normal'|'limited'
  const [customersById, setCustomersById] = useState({}); // cpf -> name

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) { navigate('/admin/login'); return; }
    load();
    loadTotals();
    loadCustomers();
  }, [navigate]);

  const loadTotals = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/credit-bags/summary/totals`);
      setTotals(r.data);
    } catch (e) { console.error(e); }
  };

  const loadCustomers = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/customers/admin/list`);
      const map = {};
      (r.data || []).forEach(c => {
        const cpf = (c.cpf || '').replace(/\D/g, '');
        if (cpf) map[cpf] = c.full_name || c.name || '';
      });
      setCustomersById(map);
    } catch (e) { /* ignore */ }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/credit-bags/bags/all`);
      setAllBags(res.data.bags || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const doSearch = async () => {
    const cpf = (searchCpf || '').replace(/\D/g, '');
    if (cpf.length !== 11) { setMsg({ type: 'error', text: 'Informe um CPF de 11 dígitos.' }); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/credit-bags/${cpf}`);
      setSelectedData(res.data);
      setMsg(null);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Erro ao buscar bolsas.' });
    }
  };

  const openAdjust = (cpf, kind, type='credit') => {
    setAdjustForm({ cpf, kind, amount: '', type, note: '' });
    setShowAdjust(true);
  };

  const submitAdjust = async () => {
    const cpf = (adjustForm.cpf || '').replace(/\D/g, '');
    const amt = parseFloat(adjustForm.amount || 0);
    if (cpf.length !== 11 || !amt || amt <= 0) { setMsg({ type: 'error', text: 'CPF ou valor inválido.' }); return; }
    const signed = adjustForm.type === 'debit' ? -amt : amt;
    try {
      await axios.post(`${BACKEND_URL}/api/credit-bags/adjust`, {
        cpf, kind: adjustForm.kind, amount: signed, source: 'admin_manual', note: adjustForm.note || null
      });
      setShowAdjust(false);
      setMsg({ type: 'success', text: 'Ajuste registrado.' });
      // refresh
      load();
      loadTotals();
      if (selectedData && selectedData.cpf === cpf) doSearch();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Erro ao ajustar saldo.' });
    }
  };

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Bolsas de Crédito"
        description="Créditos Normal e Limitada vinculados ao CPF"
        Icon={Wallet}
        iconClassName="text-teal-700"
      />

      <AdminPageBody className="space-y-6">
        {msg && (
          <div className={`p-3 rounded ${msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {msg.text}
          </div>
        )}

        {/* Cards consolidados no topo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="credit-bags-totals">
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-emerald-600 rounded-lg"><Wallet className="w-5 h-5 text-white" /></div>
                  <span className="text-sm font-bold text-emerald-800 uppercase tracking-wide">Bolsa de Crédito</span>
                </div>
                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Utilização integral</span>
              </div>
              <p className="text-3xl font-bold text-emerald-900 mt-3" data-testid="total-normal">{formatBRL(totals.normal_total)}</p>
              <p className="text-xs text-emerald-700 mt-1">{totals.normal_count} cliente(s) com saldo</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-amber-600 rounded-lg"><Wallet className="w-5 h-5 text-white" /></div>
                  <span className="text-sm font-bold text-amber-800 uppercase tracking-wide">Bolsa de Crédito Limitada</span>
                </div>
                <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Máx. 30% do valor da nova compra</span>
              </div>
              <p className="text-3xl font-bold text-amber-900 mt-3" data-testid="total-limited">{formatBRL(totals.limited_total)}</p>
              <p className="text-xs text-amber-700 mt-1">{totals.limited_count} cliente(s) com saldo</p>
            </CardContent>
          </Card>
        </div>

        {/* Busca por CPF */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> Consultar por CPF</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="search-cpf-input"
                value={searchCpf}
                onChange={(e) => setSearchCpf(e.target.value)}
                placeholder="000.000.000-00"
                className="sm:max-w-xs"
              />
              <Button onClick={doSearch} data-testid="search-cpf-btn">Buscar</Button>
              <Button variant="outline" onClick={() => openAdjust(searchCpf.replace(/\D/g,''), 'normal', 'credit')} data-testid="new-adjust-btn">
                <Plus className="w-4 h-4 mr-1" /> Novo lançamento
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bolsas do CPF selecionado */}
        {selectedData && (
          <Card>
            <CardContent className="p-4 space-y-4">
              <h3 className="font-bold text-gray-900">
                CPF: <span className="font-mono">{formatCpf(selectedData.cpf)}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Normal */}
                <div className="border rounded-lg p-4 bg-emerald-50 border-emerald-200">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-emerald-600">Bolsa Normal</Badge>
                    <p className="text-xs text-emerald-800">Utilização integral</p>
                  </div>
                  <p className="text-3xl font-bold text-emerald-900" data-testid="normal-balance">
                    {formatBRL(selectedData.normal?.balance)}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => openAdjust(selectedData.cpf, 'normal', 'credit')} data-testid="normal-credit-btn">
                      <Plus className="w-3 h-3 mr-1" /> Creditar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openAdjust(selectedData.cpf, 'normal', 'debit')} data-testid="normal-debit-btn">
                      <Minus className="w-3 h-3 mr-1" /> Debitar
                    </Button>
                  </div>
                </div>
                {/* Limitada */}
                <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-amber-600">Bolsa Limitada</Badge>
                    <p className="text-xs text-amber-800">Usar até 30% do valor da nova viagem</p>
                  </div>
                  <p className="text-3xl font-bold text-amber-900" data-testid="limited-balance">
                    {formatBRL(selectedData.limited?.balance)}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => openAdjust(selectedData.cpf, 'limited', 'credit')} data-testid="limited-credit-btn">
                      <Plus className="w-3 h-3 mr-1" /> Creditar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openAdjust(selectedData.cpf, 'limited', 'debit')} data-testid="limited-debit-btn">
                      <Minus className="w-3 h-3 mr-1" /> Debitar
                    </Button>
                  </div>
                </div>
              </div>

              {/* Histórico */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <History className="w-4 h-4" /> Últimas transações
                </h4>
                {(!selectedData.transactions || selectedData.transactions.length === 0) ? (
                  <p className="text-sm text-gray-500 text-center py-4">Nenhuma transação registrada.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left">Data</th>
                          <th className="p-2 text-left">Bolsa</th>
                          <th className="p-2 text-left">Tipo</th>
                          <th className="p-2 text-right">Valor</th>
                          <th className="p-2 text-right">Saldo após</th>
                          <th className="p-2 text-left">Origem/Nota</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedData.transactions.map(tx => (
                          <tr key={tx.id} className="border-b">
                            <td className="p-2 text-xs">{new Date(tx.created_at).toLocaleString('pt-BR')}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={tx.kind === 'limited' ? 'border-amber-500 text-amber-700' : 'border-emerald-500 text-emerald-700'}>
                                {tx.kind === 'limited' ? 'Limitada' : 'Normal'}
                              </Badge>
                            </td>
                            <td className="p-2">
                              <span className={tx.type === 'credit' ? 'text-green-700' : 'text-red-700'}>
                                {tx.type === 'credit' ? 'Crédito' : 'Débito'}
                              </span>
                            </td>
                            <td className={`p-2 text-right font-bold ${tx.type === 'credit' ? 'text-green-700' : 'text-red-700'}`}>
                              {tx.type === 'credit' ? '+' : '−'}{formatBRL(tx.amount)}
                            </td>
                            <td className="p-2 text-right">{formatBRL(tx.balance_after)}</td>
                            <td className="p-2 text-xs text-gray-600">{tx.source}{tx.note ? ` — ${tx.note}` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista global de bolsas com saldo (recolhível) */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900">Bolsas por cliente ({allBags.length})</h3>
              <Button variant="outline" size="sm" onClick={() => setShowList(v => !v)} data-testid="toggle-bags-list">
                {showList ? 'Ocultar' : 'Mostrar bolsas/clientes'}
              </Button>
            </div>
            {showList && (
              <>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <Input
                    placeholder="Pesquisar por nome ou CPF"
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    className="sm:max-w-xs"
                    data-testid="list-search-input"
                  />
                  <Select value={listKindFilter} onValueChange={setListKindFilter}>
                    <SelectTrigger className="sm:max-w-xs" data-testid="list-kind-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as bolsas</SelectItem>
                      <SelectItem value="normal">Somente Normal</SelectItem>
                      <SelectItem value="limited">Somente Limitada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {loading ? (
                  <p className="text-sm text-gray-500">Carregando…</p>
                ) : (
                  (() => {
                    const term = (listSearch || '').toLowerCase().replace(/\D/g,'');
                    const nameTerm = (listSearch || '').toLowerCase();
                    const filtered = allBags.filter(b => {
                      if (listKindFilter !== 'all' && b.kind !== listKindFilter) return false;
                      if (!listSearch) return true;
                      const cpf = (b.cpf || '').replace(/\D/g,'');
                      const name = (customersById[cpf] || '').toLowerCase();
                      return cpf.includes(term) || name.includes(nameTerm);
                    });
                    if (filtered.length === 0) {
                      return <p className="text-sm text-gray-500 text-center py-4" data-testid="empty-bags">Nenhuma bolsa encontrada.</p>;
                    }
                    return (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="p-2 text-left">Cliente</th>
                              <th className="p-2 text-left">CPF</th>
                              <th className="p-2 text-left">Bolsa</th>
                              <th className="p-2 text-right">Saldo</th>
                              <th className="p-2 text-left">Atualizado</th>
                              <th className="p-2 text-left">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((b, idx) => {
                              const cpfClean = (b.cpf || '').replace(/\D/g,'');
                              const cname = customersById[cpfClean] || '-';
                              return (
                                <tr key={`${b.cpf}-${b.kind}-${idx}`} className="border-b hover:bg-gray-50">
                                  <td className="p-2">{cname}</td>
                                  <td className="p-2 font-mono">{formatCpf(b.cpf)}</td>
                                  <td className="p-2">
                                    <Badge className={b.kind === 'limited' ? 'bg-amber-600' : 'bg-emerald-600'}>
                                      {b.kind === 'limited' ? 'Limitada' : 'Normal'}
                                    </Badge>
                                  </td>
                                  <td className="p-2 text-right font-bold">{formatBRL(b.balance)}</td>
                                  <td className="p-2 text-xs">{new Date(b.updated_at).toLocaleString('pt-BR')}</td>
                                  <td className="p-2">
                                    <Button size="sm" variant="outline" onClick={() => { setSearchCpf(b.cpf); setTimeout(doSearch, 0); }}>
                                      Ver histórico
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()
                )}
              </>
            )}
          </CardContent>
        </Card>

      {/* Dialog de ajuste */}
      <Dialog open={showAdjust} onOpenChange={setShowAdjust}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajustar saldo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>CPF</Label>
              <Input
                data-testid="adjust-cpf-input"
                value={adjustForm.cpf}
                onChange={(e) => setAdjustForm(f => ({ ...f, cpf: e.target.value }))}
                placeholder="Só números"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Tipo de Bolsa</Label>
                <Select value={adjustForm.kind} onValueChange={(v) => setAdjustForm(f => ({ ...f, kind: v }))}>
                  <SelectTrigger data-testid="adjust-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="limited">Limitada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Operação</Label>
                <Select value={adjustForm.type} onValueChange={(v) => setAdjustForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="adjust-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Creditar</SelectItem>
                    <SelectItem value="debit">Debitar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Valor (R$)</Label>
              <Input
                data-testid="adjust-amount-input"
                type="number"
                min="0"
                step="0.01"
                value={adjustForm.amount}
                onChange={(e) => setAdjustForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0,00"
              />
            </div>
            <div>
              <Label>Observação (opcional)</Label>
              <Input
                data-testid="adjust-note-input"
                value={adjustForm.note}
                onChange={(e) => setAdjustForm(f => ({ ...f, note: e.target.value }))}
                placeholder="Motivo ou referência"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAdjust(false)} className="flex-1">Cancelar</Button>
              <Button onClick={submitAdjust} className="flex-1 bg-[#003366]" data-testid="adjust-submit-btn">Confirmar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </AdminPageBody>
    </AdminPageShell>
  );
};

export default AdminCreditBags;
